import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import routes from "./routes";
import ConditionalRoute from "@/components/ConditionalRoute/ConditionalRoute";
import PreLogin from "@/layouts/PreLogin/PreLogin";
import PostLogin from "@/layouts/PostLogin/PostLogin";
import PageNotFound from "@/pages/PageNotFound/PageNotFound";
import { AUTH_ROUTE_PATHS } from "@/pages/Auth/authConfig";

export interface RouteConfig {
  path: string;
  element?: React.ReactNode; // make optional
  children?: Record<string, RouteConfig> | RouteConfig[];
  pageName?: string;
  sidebar?: { show: boolean };
  permissionName?: string;
  
}

const AuthOutlet = () => <Outlet />;

const {
  WELCOME,
  QUESTIONS,
  INTRODUCTION,
  ...appPrivateRoutes
} = routes.privateRoutes;

const privateOnboardingRoutes = { WELCOME, QUESTIONS, INTRODUCTION };

const buildRoutes = (
  routesInput: Record<string, RouteConfig> | RouteConfig[]
): RouteObject[] => {
  const routesArray: RouteConfig[] = Array.isArray(routesInput)
    ? routesInput
    : Object.values(routesInput);

  return routesArray.map(({ path, element, children }) => ({
    path,
    ...(element ? { element } : {}), // only include element if it exists
    ...(children ? { children: buildRoutes(children) } : {}),
  }));
};


const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Navigate
        to={AUTH_ROUTE_PATHS.getStarted}
        replace
      />
    ),
  },

  // Open routes
  ...buildRoutes(routes.routes),

  // Pre-login layout: public auth + authenticated onboarding (welcome, questions, introduction)
  {
    element: <ConditionalRoute component={PreLogin} />,
    children: [
      ...buildRoutes(routes.publicRoutes),
      {
        element: <ConditionalRoute isPrivate component={AuthOutlet} />,
        children: buildRoutes(privateOnboardingRoutes),
      },
    ],
  },

  // Post-login layout: authenticated app pages
  {
    element: <ConditionalRoute isPrivate component={PostLogin} />,
    children: [
      ...buildRoutes(appPrivateRoutes),
      {
        path: "/",
        element: <Navigate replace to={routes.privateRoutes.HOME.path} />,
      },
    ],
  },

  // Catch-all
  {
    path: "*",
    element: <PageNotFound />,
  },
]);

export default router;
